import { useMemo } from 'react';
import { Button, Input, Modal } from '../../../../../common/_custom';
import { EMAIL, NAME, PHONE, TRIGGER_NOTIFICATION, JOB_ROLE } from '../../../../../utils/constants/companyLogin';
import styles from './EditUserModal.module.scss';
import CountryCodesDropdown, { CountryCodeData } from '../../../../Company/CompanyLogin/CountryCodesDropdown/CountryCodesDropdown';
import Dropdown from '../../../../../common/_custom/Dropdown';
import { DEFAULT_JOB_ROLES } from './AddUserModal';
import CheckBox from '../../../../../common/CheckBox/CheckBox';
import { useEditUserModal } from './useEditUserModal';
import { useVisibilityConfig } from '../../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../../utils/constants/visibilityKeys';
import { USER_TYPE_OPTIONS, buildRoleOptions } from '../../ManageUserConstants';

interface EditUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    userData: any;
    allRowsData?: any;
    onUsersUpdate?: (updatedUsers: any[]) => void;
    countryCodes: CountryCodeData[];
    isCompanySide: boolean;
    allowUserTypeEdit?: boolean;
}

const EditUserModal = ({
    isOpen,
    onClose,
    userData,
    allRowsData,
    onUsersUpdate,
    countryCodes,
    isCompanySide,
    allowUserTypeEdit = true,
}: EditUserModalProps) => {
    const { canWrite, roleCatalog } = useVisibilityConfig();
    const canEditRole = canWrite(VISIBILITY_KEYS.USER_MANAGEMENT.EDIT_USER_ROLE);
    const catalogResult = useMemo(() => (Object.keys(roleCatalog ?? {}).length > 0 ? buildRoleOptions(roleCatalog) : null), [roleCatalog]);
    const teamRoleOptions = catalogResult ? catalogResult.options : USER_TYPE_OPTIONS;
    const defaultTeamRoleId = catalogResult ? catalogResult.defaultId : undefined;

    const {
        loading,
        formData,
        formErrors,
        handleFormChange,
        handleSave,
        handleConfirmDemotion,
        handleCancelDemotion,
        onClosedModal,
        isSaveDisabled,
        showDemotionConfirm,
        USER_ROLE_KEY,
    } = useEditUserModal({
        isOpen,
        userData,
        allRowsData,
        onUsersUpdate,
        countryCodes,
        onClose,
        isCompanySide,
        canEditRole,
        teamRoleOptions,
        defaultTeamRoleId,
        allowUserTypeEdit,
    });

    if (showDemotionConfirm) {
        return (
            <Modal open={isOpen} onClose={onClosedModal} size="flexible" persistent style={{ padding: 0 }}>
                <div className={styles.ConfirmContainer}>
                    <div className={styles.ConfirmHeader}>
                        <div className={styles.ConfirmTitle}>Change Your Role?</div>
                        <div className={styles.ConfirmSubtitle}>
                            You are about to change your own role from Super Admin. You may lose administrative access. Are you sure?
                        </div>
                    </div>
                    <div className={styles.ConfirmFooter}>
                        <Button
                            text="Cancel"
                            variant="secondary"
                            onClick={handleCancelDemotion}
                            disabled={loading}
                            style={{ flex: 1, margin: 0 }}
                        />
                        <Button
                            text="Confirm"
                            variant="orange"
                            onClick={handleConfirmDemotion}
                            loading={loading}
                            disabled={loading}
                            style={{ flex: 1, margin: 0 }}
                        />
                    </div>
                </div>
            </Modal>
        );
    }

    return (
        <Modal open={isOpen} onClose={onClosedModal} size="flexible" persistent style={{ padding: 0 }}>
            <div className={styles.ModalBody}>
                <h2>Edit User</h2>
                <Input
                    value={formData[NAME]}
                    onChange={(val) => handleFormChange(val, NAME)}
                    placeholder="Full Name"
                    style={{ marginTop: '30px' }}
                    isValid={!formErrors[NAME]}
                    errorMessage={formErrors[NAME]}
                    allowAllCharacters={true}
                    disabled={!isCompanySide}
                />
                <Input
                    value={formData[EMAIL]}
                    onChange={(val) => handleFormChange(val, EMAIL)}
                    placeholder="Email ID"
                    style={{ marginTop: '30px' }}
                    isValid={!formErrors[EMAIL]}
                    errorMessage={formErrors[EMAIL]}
                    allowAllCharacters={true}
                    disabled={!isCompanySide}
                />
                <Dropdown
                    optionsList={DEFAULT_JOB_ROLES}
                    selectedOption={formData[JOB_ROLE] || null}
                    itemClickHandler={(option) => {
                        handleFormChange(option, JOB_ROLE);
                    }}
                    label="Job Role"
                    placeholder="Job Role"
                    enableSearch={true}
                    hideOtherOptionWhenNoMatch={true}
                    customOption={{
                        enabled: true,
                        text: 'Add Other Job Role',
                        placeholder: 'Enter job role...',
                        maxLength: 50,
                    }}
                    style={{ marginTop: '30px' }}
                />
                {allowUserTypeEdit && canEditRole && (
                    <Dropdown
                        optionsList={teamRoleOptions}
                        selectedOption={formData[USER_ROLE_KEY] || null}
                        itemClickHandler={(option) => handleFormChange(option, USER_ROLE_KEY)}
                        label="User Type"
                        placeholder="User Type"
                        style={{ marginTop: '1.875rem' }}
                        hideClearIcon
                    />
                )}
                <div className={styles.Row}>
                    <Input
                        placeholder="Phone Number"
                        value={formData[PHONE]}
                        onChange={(value) => {
                            if (/^\d*$/.test(value) && value.length <= 10) {
                                handleFormChange(value, PHONE);
                            }
                        }}
                        style={{ marginTop: '0', width: '100%' }}
                        type="number"
                        isValid={!formErrors[PHONE]}
                        errorMessage={formErrors[PHONE]}
                        disabled={!isCompanySide}
                    />
                </div>
                <CheckBox
                    label={'Send invite on email'}
                    checked={formData[TRIGGER_NOTIFICATION]}
                    onCheck={(val) => handleFormChange(val, TRIGGER_NOTIFICATION)}
                    variant={'tertiary'}
                    style={{ padding: '1.5rem 0 0rem 0.25rem' }}
                />
            </div>
            <div className={styles.Bottom}>
                <Button
                    onClick={handleSave}
                    text={'Save'}
                    variant="purple"
                    loading={loading}
                    style={{ width: '100%' }}
                    disabled={isSaveDisabled}
                />
            </div>
        </Modal>
    );
};

export default EditUserModal;
